import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import Hls from 'hls.js';
import { PlayArrowIcon, PauseIcon, VolumeUpIcon, VolumeOffIcon, FullscreenIcon } from '../icons';
import styles from './CustomVideoPlayer.module.scss';

interface CustomVideoPlayerProps {
    src: string;
    className?: string;
    autoPlay?: boolean;
    playsInline?: boolean;
    muted?: boolean;
    preload?: 'none' | 'metadata' | 'auto';
    onLoadedMetadata?: (event: React.SyntheticEvent<HTMLVideoElement>) => void;
    onLoadedData?: (event: React.SyntheticEvent<HTMLVideoElement>) => void;
    style?: React.CSSProperties;
    poster?: string;
    showControls?: boolean;
    // Новые пропы для качества
    videoQuality?: 'auto' | 'high' | 'medium' | 'low';
    enableHardwareAcceleration?: boolean;
    maxResolution?: { width: number; height: number };
}

export interface CustomVideoPlayerRef {
    video: HTMLVideoElement | null;
    hls: Hls | null;
    play: () => void;
    pause: () => void;
    togglePlay: () => void;
    toggleMute: () => void;
    toggleFullscreen: () => void;
}

const CustomVideoPlayer = forwardRef<CustomVideoPlayerRef, CustomVideoPlayerProps>(({
    src,
    className,
    autoPlay = false,
    playsInline = true,
    muted = false,
    preload = 'metadata',
    onLoadedMetadata,
    onLoadedData,
    style,
    poster,
    showControls = true,
    videoQuality = 'auto',
    enableHardwareAcceleration = true,
    maxResolution
}, ref) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const hlsRef = useRef<Hls | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const progressRef = useRef<HTMLDivElement>(null);
    const volumeRef = useRef<HTMLDivElement>(null);

    const [isPlaying, setIsPlaying] = useState(false);
    const [isMuted, setIsMuted] = useState(muted);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(1);
    const [showVolumeSlider, setShowVolumeSlider] = useState(false);
    const [, setIsDragging] = useState(false);

    useImperativeHandle(ref, () => ({
        video: videoRef.current,
        hls: hlsRef.current,
        play: () => videoRef.current?.play(),
        pause: () => videoRef.current?.pause(),
        togglePlay: () => {
            if (videoRef.current) {
                if (videoRef.current.paused) {
                    videoRef.current.play();
                } else {
                    videoRef.current.pause();
                }
            }
        },
        toggleMute: () => {
            if (videoRef.current) {
                videoRef.current.muted = !videoRef.current.muted;
                setIsMuted(videoRef.current.muted);
            }
        },
        toggleFullscreen: () => {
            if (!document.fullscreenElement) {
                containerRef.current?.requestFullscreen();
            } else {
                document.exitFullscreen();
            }
        }
    }), []);

    // HLS инициализация с улучшенными настройками
    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        // Включаем аппаратное ускорение (убран поворот)
        if (enableHardwareAcceleration) {
            video.style.transform = 'translateZ(0)';
            video.style.willChange = 'transform';
        }

        // Очищаем предыдущий HLS instance
        if (hlsRef.current) {
            hlsRef.current.destroy();
            hlsRef.current = null;
        }

        // Проверяем, является ли URL HLS-потоком
        const isHLS = src.includes('.m3u8') || src.includes('playlist.m3u8');

        if (isHLS) {
            // Используем HLS.js для HLS потоков
            if (Hls.isSupported()) {
                const hlsConfig: any = {
                    enableWorker: true,
                    lowLatencyMode: false, // Отключаем для лучшего качества
                    backBufferLength: 90,
                    maxBufferLength: 30,
                    maxMaxBufferLength: 60,
                    // Настройки качества - принудительно высокое качество
                    abrEwmaFastLive: 1.0, // Быстрее переключается на высокое качество
                    abrEwmaSlowLive: 3.0, // Медленнее понижает качество
                    abrEwmaFastVoD: 1.0,
                    abrEwmaSlowVoD: 3.0,
                    abrBandWidthFactor: 0.8, // Более агрессивно выбирает высокое качество
                    abrBandWidthUpFactor: 0.9, // Быстрее повышает качество
                    abrMaxWithRealBitrate: false, // Не ограничиваем битрейт
                    // Настройки для высокого качества
                    maxLoadingDelay: 8, // Увеличиваем время загрузки для лучшего качества
                    maxBufferHole: 0.3, // Меньше пропусков в буфере
                    highBufferWatchdogPeriod: 1, // Чаще проверяем буфер
                    nudgeOffset: 0.05, // Более точная синхронизация
                    nudgeMaxRetry: 5, // Больше попыток восстановления
                    maxFragLoadingTimeMs: 30000, // Больше времени на загрузку фрагментов
                    fragLoadingTimeOut: 30000,
                    manifestLoadingTimeOut: 15000,
                    manifestLoadingMaxRetry: 6,
                    manifestLoadingRetryDelay: 500,
                    levelLoadingTimeOut: 15000,
                    levelLoadingMaxRetry: 6,
                    levelLoadingRetryDelay: 500,
                    // Принудительное высокое качество
                    startLevel: -1, // Начинаем с самого высокого доступного уровня
                    capLevelToPlayerSize: false, // Не ограничиваем качество размером плеера
                    ignoreDevicePixelRatio: false, // Учитываем плотность пикселей
                    // Настройки для стабильного воспроизведения
                    liveSyncDurationCount: 3, // Больше буфер для живого потока
                    liveMaxLatencyDurationCount: 10, // Больше задержка для стабильности
                    liveDurationInfinity: true, // Не ограничиваем длительность живого потока
                    // Настройки для лучшего качества видео
                    renderTextTracksNatively: true, // Нативный рендеринг субтитров
                    preferManagedMediaSource: true, // Предпочитаем управляемый MediaSource
                    enableSoftwareAES: false, // Используем аппаратное AES
                    debug: false // Отключаем отладку для производительности
                };

                const hls = new Hls(hlsConfig);
                hlsRef.current = hls;
                hls.loadSource(src);
                hls.attachMedia(video);

                hls.on(Hls.Events.MANIFEST_PARSED, (event, data) => {
                    console.log('HLS manifest parsed, available levels:', data.levels);

                    // Принудительно устанавливаем максимальное качество
                    const levels = data.levels;
                    if (levels && levels.length > 0) {
                        // Всегда выбираем самый высокий уровень качества
                        const highestLevel = levels.length - 1;
                        hls.currentLevel = highestLevel;
                        console.log(`Forced highest quality level: ${highestLevel}`, levels[highestLevel]);

                        // Принудительно блокируем переключение качества
                        hls.on(Hls.Events.LEVEL_SWITCHING, (event, data) => {
                            // Принудительно возвращаемся к максимальному качеству
                            if (data.level !== highestLevel) {
                                hls.currentLevel = highestLevel;
                                console.log('Forced back to highest quality level');
                            }
                        });
                        console.log('Quality switching blocked - using highest quality only');
                    }

                    if (autoPlay) {
                        video.play().catch(console.error);
                    }
                });

                // Обработчик изменения уровня качества
                hls.on(Hls.Events.LEVEL_SWITCHED, (event, data) => {
                    console.log('HLS level switched to:', data.level, hls.levels[data.level]);
                });

                hls.on(Hls.Events.ERROR, (event, data) => {
                    console.error('HLS error:', data);
                    if (data.fatal) {
                        switch (data.type) {
                            case Hls.ErrorTypes.NETWORK_ERROR:
                                console.log('HLS network error, trying to recover...');
                                hls.startLoad();
                                break;
                            case Hls.ErrorTypes.MEDIA_ERROR:
                                console.log('HLS media error, trying to recover...');
                                hls.recoverMediaError();
                                break;
                            default:
                                console.log('HLS fatal error, destroying instance');
                                hls.destroy();
                                break;
                        }
                    }
                });
            } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
                // Нативная поддержка HLS (Safari)
                video.src = src;
                if (autoPlay) {
                    video.play().catch(console.error);
                }
            } else {
                console.error('HLS не поддерживается в этом браузере');
                // Пытаемся воспроизвести как обычное видео
                video.src = src;
            }
        } else {
            // Обычное видео (MP4, WebM, etc.) - настройки для максимального качества
            video.src = src;

            // Настройки для лучшего качества обычного видео
            video.setAttribute('playsinline', 'true');
            video.setAttribute('webkit-playsinline', 'true');
            video.setAttribute('x-webkit-airplay', 'allow');

            // Принудительно устанавливаем высокое качество
            if (video.canPlayType('video/mp4; codecs="avc1.42E01E"')) {
                console.log('Using H.264 high profile for better quality');
            }

            if (autoPlay) {
                video.play().catch(console.error);
            }
        }

        return () => {
            if (hlsRef.current) {
                hlsRef.current.destroy();
                hlsRef.current = null;
            }
        };
    }, [src, autoPlay, videoQuality, enableHardwareAcceleration]);

    // Обработчики событий видео
    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        const handlePlay = () => setIsPlaying(true);
        const handlePause = () => setIsPlaying(false);
        const handleTimeUpdate = () => setCurrentTime(video.currentTime);
        const handleDurationChange = () => setDuration(video.duration);
        const handleVolumeChange = () => {
            setVolume(video.volume);
            setIsMuted(video.muted);
        };

        video.addEventListener('play', handlePlay);
        video.addEventListener('pause', handlePause);
        video.addEventListener('timeupdate', handleTimeUpdate);
        video.addEventListener('durationchange', handleDurationChange);
        video.addEventListener('volumechange', handleVolumeChange);

        return () => {
            video.removeEventListener('play', handlePlay);
            video.removeEventListener('pause', handlePause);
            video.removeEventListener('timeupdate', handleTimeUpdate);
            video.removeEventListener('durationchange', handleDurationChange);
            video.removeEventListener('volumechange', handleVolumeChange);
        };
    }, []);


    const handlePlayPause = () => {
        if (videoRef.current) {
            if (videoRef.current.paused) {
                videoRef.current.play();
            } else {
                videoRef.current.pause();
            }
        }
    };

    const handleMuteToggle = () => {
        if (videoRef.current) {
            videoRef.current.muted = !videoRef.current.muted;
        }
    };

    const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newVolume = parseFloat(e.target.value);
        if (videoRef.current) {
            videoRef.current.volume = newVolume;
            videoRef.current.muted = newVolume === 0;
        }
    };

    const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!videoRef.current || !progressRef.current) return;

        const rect = progressRef.current.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const percentage = clickX / rect.width;
        const newTime = percentage * duration;

        videoRef.current.currentTime = newTime;
    };

    const handleProgressMouseDown = () => {
        setIsDragging(true);
    };

    const handleProgressMouseUp = () => {
        setIsDragging(false);
    };

    const handleFullscreen = () => {
        if (!document.fullscreenElement) {
            containerRef.current?.requestFullscreen();
        } else {
            document.exitFullscreen();
        }
    };

    const formatTime = (time: number) => {
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    const progressPercentage = duration > 0 ? (currentTime / duration) * 100 : 0;

    return (
        <div
            ref={containerRef}
            className={`${styles.videoContainer} ${maxResolution ? styles.maxResolution : ''} ${className || ''}`}
            style={style}
        >
            <video
                ref={videoRef}
                className={styles.video}
                playsInline={playsInline}
                muted={muted}
                preload={preload}
                onLoadedMetadata={onLoadedMetadata}
                onLoadedData={onLoadedData}
                poster={poster}
                // Добавляем атрибуты для лучшего качества
                style={{
                    ...style,
                    imageRendering: '-webkit-optimize-contrast'
                }}
            />

            {showControls && (
                <div className={styles.controls}>
                    <div className={styles.progressContainer}>
                        <div
                            ref={progressRef}
                            className={styles.progressBar}
                            onClick={handleProgressClick}
                            onMouseDown={handleProgressMouseDown}
                            onMouseUp={handleProgressMouseUp}
                        >
                            <div
                                className={styles.progress}
                                style={{ width: `${progressPercentage}%` }}
                            />
                        </div>
                    </div>

                    <div className={styles.controlsRow}>
                        <div className={styles.leftControls}>
                            <button
                                className={styles.controlButton}
                                onClick={handlePlayPause}
                                aria-label={isPlaying ? 'Pause' : 'Play'}
                            >
                                {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                            </button>

                            <div className={styles.volumeContainer}>
                                <button
                                    className={styles.controlButton}
                                    onClick={handleMuteToggle}
                                    onMouseEnter={() => setShowVolumeSlider(true)}
                                    onMouseLeave={() => setShowVolumeSlider(false)}
                                    aria-label={isMuted ? 'Unmute' : 'Mute'}
                                >
                                    {isMuted || volume === 0 ? <VolumeOffIcon /> : <VolumeUpIcon />}
                                </button>

                                {showVolumeSlider && (
                                    <div
                                        ref={volumeRef}
                                        className={styles.volumeSlider}
                                        onMouseEnter={() => setShowVolumeSlider(true)}
                                        onMouseLeave={() => setShowVolumeSlider(false)}
                                    >
                                        <input
                                            type="range"
                                            min="0"
                                            max="1"
                                            step="0.1"
                                            value={isMuted ? 0 : volume}
                                            onChange={handleVolumeChange}
                                            className={styles.volumeInput}
                                        />
                                    </div>
                                )}
                            </div>

                            <div className={styles.timeDisplay}>
                                {formatTime(currentTime)} / {formatTime(duration)}
                            </div>
                        </div>

                        <div className={styles.rightControls}>
                            <button
                                className={styles.controlButton}
                                onClick={handleFullscreen}
                                aria-label="Fullscreen"
                            >
                                <FullscreenIcon />
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
});

CustomVideoPlayer.displayName = 'CustomVideoPlayer';

export default CustomVideoPlayer;
